import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { regions, propertyTypes, grades } from "@/mocks/auctionItems";
import { useAuctionItems } from "@/hooks/useAuctionItems";
import { useCaseSearch } from "@/hooks/useCaseSearch";
import LiveCaseCard from "./LiveCaseCard";
import { motion } from "motion/react";
import { triggerOpenChatbotWithMessage } from "@/hooks/useChatbotTrigger";
import { buildAnalysisMessage } from "@/utils/auctionHelpers";

// AuctionFilter의 "물건" 칩(한글)을 searchCases type 파라미터로 — API가 받는
// 6종 목록에 없는 값("상가")은 undefined로 둬서 지역만으로 검색되게 한다.
const PROPERTY_TYPE_TO_SEARCH: Record<string, string | undefined> = {
  아파트: "아파트",
  다세대: "다세대",
  오피스텔: "오피스텔",
  단독주택: "단독",
  상가: undefined,
};

// 검색어 비교용 — 공백·하이픈·마침표·"타경"을 지워 "2025 타경 63332"·"2025-타경-63332"·
// "2025타경63332"·"2025-63332"(타경 생략) 어떻게 입력해도 같은 값으로 취급한다.
function normalizeSearch(s: string): string {
  return s.toLowerCase().replace(/[\s\-.]/g, "").replace(/타경/g, "");
}

const investmentRanges = ["전체", "3천만 이하", "5천만 이하", "1억 이하"];

/** 카드용 입찰계산기 드롭다운 버튼 */
function CalcDropdown({ itemId }: { itemId: string }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="w-full flex items-center justify-center gap-1 py-1.5 rounded-full text-xs font-medium border border-[#C4956A] text-[#C4956A] hover:bg-[#C4956A]/5 transition-all whitespace-nowrap cursor-pointer"
      >
        <i className="ri-calculator-line text-sm"></i>
        계산하기
        <i className="ri-arrow-down-s-line text-xs"></i>
      </button>
      {open && (
        <div
          className="absolute bottom-full mb-1 right-0 w-40 bg-white border border-[#E8E5E0] rounded-xl shadow-lg overflow-hidden z-30"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              navigate(`/bid-calculator?itemId=${itemId}&mode=normal`);
            }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-[#5C5650] hover:bg-[#FAFAF8] transition-colors cursor-pointer text-left whitespace-nowrap"
          >
            <i className="ri-calculator-fill text-[#1C1917]"></i>
            일반방식
          </button>
          <div className="border-t border-[#F0EDE8]"></div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              navigate(`/bid-calculator?itemId=${itemId}&mode=reverse`);
            }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-[#5C5650] hover:bg-[#FAFAF8] transition-colors cursor-pointer text-left whitespace-nowrap"
          >
            <i className="ri-flashlight-fill text-[#C4956A]"></i>
            시세역산방식
          </button>
        </div>
      )}
    </div>
  );
}

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

export default function AuctionFilter() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [region, setRegion] = useState("전체");
  const [propertyType, setPropertyType] = useState("전체");
  const [grade, setGrade] = useState("전체");
  const [investmentRange, setInvestmentRange] = useState("전체");
  const [sortBy, setSortBy] = useState("auctionDate");
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());
  // kangstudy_auction_items(자동 큐레이션 + 관리자 등록). 조회 실패 시에만 목업.
  const { items: auctionItems, loading } = useAuctionItems();

  const filteredItems = useMemo(() => {
    let items = [...auctionItems];

    if (searchQuery) {
      // 공백·하이픈·마침표·"타경"을 지우고 비교 — "2025 타경 63332" · "2025-타경-63332" ·
      // "2025타경63332" · "2025-63332"(타경 생략) 어떻게 입력해도 같은 걸로 본다.
      const q = normalizeSearch(searchQuery);
      items = items.filter(
        (item) =>
          normalizeSearch(item.caseNumber).includes(q) ||
          normalizeSearch(item.location).includes(q) ||
          normalizeSearch(item.court).includes(q) ||
          normalizeSearch(item.propertyType).includes(q)
      );
    }

    if (region !== "전체") {
      items = items.filter((item) => item.location.includes(region));
    }

    if (propertyType !== "전체") {
      items = items.filter((item) => item.propertyType === propertyType);
    }

    if (grade !== "전체") {
      items = items.filter((item) => item.grade === grade);
    }

    if (investmentRange !== "전체") {
      if (investmentRange === "3천만 이하") {
        items = items.filter((item) => item.minimumPrice <= 30000000);
      } else if (investmentRange === "5천만 이하") {
        items = items.filter((item) => item.minimumPrice <= 50000000);
      } else if (investmentRange === "1억 이하") {
        items = items.filter((item) => item.minimumPrice <= 100000000);
      }
    }

    switch (sortBy) {
      case "priceLow":
        items.sort((a, b) => a.minimumPrice - b.minimumPrice);
        break;
      case "priceHigh":
        items.sort((a, b) => b.minimumPrice - a.minimumPrice);
        break;
      case "profit":
        // 시세 추정이 없는 물건(expectedProfit null)은 뒤로 보낸다.
        items.sort((a, b) => (b.expectedProfit ?? -Infinity) - (a.expectedProfit ?? -Infinity));
        break;
      case "auctionDate":
      default:
        items.sort(
          (a, b) =>
            new Date(a.auctionDate).getTime() -
            new Date(b.auctionDate).getTime()
        );
        break;
    }

    return items;
  }, [auctionItems, searchQuery, region, propertyType, grade, investmentRange, sortBy]);

  // 강스터디 큐레이션 60건 밖에도 매물이 있다 — EchoTwin 전체 매물 풀에서 같은
  // 조건으로 항상 실시간 검색해 따로 보여준다(필터를 고르지 않아도 기본 노출,
  // 2026-08-26부터).
  const liveSearch = useCaseSearch(
    {
      region: region !== "전체" ? region : undefined,
      type: propertyType !== "전체" ? PROPERTY_TYPE_TO_SEARCH[propertyType] : undefined,
    },
    true,
  );

  return (
    <div>
      {/* Search & Sort Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4, ease: "easeOut" as const }}
        className="flex flex-col sm:flex-row gap-3 mb-6"
      >
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780]"></i>
          <input
            type="text"
            placeholder="사건번호, 소재지, 법원으로 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white text-[#5C5650] focus:outline-none focus:border-[#C4956A] cursor-pointer"
        >
          <option value="auctionDate">입찰일 빠른순</option>
          <option value="priceLow">최저가 낮은순</option>
          <option value="priceHigh">최저가 높은순</option>
          <option value="profit">예상수익 높은순</option>
        </select>
        <button
          onClick={() => setShowMobileFilter(!showMobileFilter)}
          className="lg:hidden flex items-center gap-2 px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white text-[#5C5650] hover:border-[#C4956A] transition-colors whitespace-nowrap"
        >
          <i className="ri-filter-3-line"></i>
          필터
        </button>
      </motion.div>

      {/* Filter Chips */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.4, ease: "easeOut" as const }}
        className={`flex flex-wrap items-center gap-2 mb-8 ${
          showMobileFilter ? "flex" : "hidden lg:flex"
        }`}
      >
        {/* Region Filter */}
        <div className="flex items-center gap-1 bg-[#FAFAF8] rounded-full px-1 py-1 border border-[#E8E5E0]">
          <span className="text-xs text-[#8C8780] pl-2 pr-1 whitespace-nowrap">지역</span>
          {regions.map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                region === r
                  ? "bg-[#C4956A] text-white font-medium"
                  : "text-[#5C5650] hover:bg-[#E8E5E0]"
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        {/* Property Type Filter */}
        <div className="flex items-center gap-1 bg-[#FAFAF8] rounded-full px-1 py-1 border border-[#E8E5E0]">
          <span className="text-xs text-[#8C8780] pl-2 pr-1 whitespace-nowrap">물건</span>
          {propertyTypes.map((t) => (
            <button
              key={t}
              onClick={() => setPropertyType(t)}
              className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                propertyType === t
                  ? "bg-[#C4956A] text-white font-medium"
                  : "text-[#5C5650] hover:bg-[#E8E5E0]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Grade Filter */}
        <div className="flex items-center gap-1 bg-[#FAFAF8] rounded-full px-1 py-1 border border-[#E8E5E0]">
          <span className="text-xs text-[#8C8780] pl-2 pr-1 whitespace-nowrap">등급</span>
          {grades.map((g) => (
            <button
              key={g}
              onClick={() => setGrade(g)}
              className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                grade === g
                  ? "bg-[#C4956A] text-white font-medium"
                  : "text-[#5C5650] hover:bg-[#E8E5E0]"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        {/* Investment Range Filter */}
        <div className="flex items-center gap-1 bg-[#FAFAF8] rounded-full px-1 py-1 border border-[#E8E5E0]">
          <span className="text-xs text-[#8C8780] pl-2 pr-1 whitespace-nowrap">실투자금</span>
          {investmentRanges.map((r) => (
            <button
              key={r}
              onClick={() => setInvestmentRange(r)}
              className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap ${
                investmentRange === r
                  ? "bg-emerald-600 text-white font-medium"
                  : "text-[#5C5650] hover:bg-[#E8E5E0]"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Results Count */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="text-xs text-[#8C8780] mb-5"
      >
        총 <strong className="text-[#1C1917]">{filteredItems.length}</strong>건의
        경매 물건
      </motion.p>

      {/* Auction Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        {filteredItems.map((item) => (
          <motion.div
            key={item.id}
            variants={cardVariants}
            whileHover={{ y: -6 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
          >
            <Link
              to={`/auctions/${item.id}`}
              className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all block"
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                {/* echotwin-api casePhotoUrl 이 404 를 줄 수 있다(id 매핑 미확인·
                    사진 미등록). onError 로 잡아 물건종류 아이콘 플레이스홀더로
                    대체한다 — 깨진 이미지 아이콘을 노출하지 않기 위함. */}
                {item.imageUrl && !imgErrors.has(item.id) ? (
                  <img loading="lazy" decoding="async"
                    src={item.imageUrl}
                    alt={`${item.propertyType} - ${item.location}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    onError={() =>
                      setImgErrors((prev) => new Set(prev).add(item.id))
                    }
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#F5F2EE] to-[#E8E5E0] flex flex-col items-center justify-center gap-2">
                    <i className="ri-building-2-line text-4xl text-[#C4B8A8]"></i>
                    <span className="text-xs text-[#A89C8C]">{item.propertyType}</span>
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span
                    className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                      item.grade === "A"
                        ? "bg-emerald-500 text-white"
                        : item.grade === "B"
                        ? "bg-amber-500 text-white"
                        : "bg-gray-400 text-white"
                    }`}
                  >
                    {item.grade}등급
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="px-2.5 py-1 text-xs font-medium bg-white/90 rounded-full text-[#1C1917]">
                    유찰 {item.failureCount}회
                  </span>
                </div>
              </div>

              {/* Badges Row */}
              {(() => {
                const badges: { label: string; colorClass: string }[] = [];
                const sortedByProfit = auctionItems
                  .filter((i) => i.expectedProfit != null)
                  .sort((a, b) => (b.expectedProfit ?? 0) - (a.expectedProfit ?? 0));
                const top3Ids = new Set(sortedByProfit.slice(0, 3).map((i) => i.id));
                if (top3Ids.has(item.id)) {
                  badges.push({ label: "오늘의 추천 Top 3", colorClass: "bg-amber-100 text-amber-800 border-amber-300" });
                }
                if (item.failureCount >= 2 && item.minimumPrice <= item.appraisedValue * 0.55) {
                  badges.push({ label: "반토막 유찰", colorClass: "bg-rose-100 text-rose-700 border-rose-300" });
                }
                if (item.failureCount === 0) {
                  badges.push({ label: "신건", colorClass: "bg-emerald-100 text-emerald-700 border-emerald-300" });
                }
                if (badges.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1.5 px-4 pt-3 pb-0">
                    {badges.map((b) => (
                      <span key={b.label} className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border ${b.colorClass}`}>
                        {b.label}
                      </span>
                    ))}
                  </div>
                );
              })()}

              {/* Content */}
              <div className="p-4 pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-[#C4956A] bg-[#C4956A]/5 px-2 py-0.5 rounded">
                    {item.propertyType}
                  </span>
                  <span className="text-xs text-[#8C8780]">{item.court}</span>
                </div>
                <h3 className="text-sm font-semibold text-[#1C1917] mb-3 line-clamp-1">
                  {item.location}
                </h3>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#8C8780]">감정가</span>
                    <span className="text-[#5C5650] font-medium">
                      {item.appraisedValue.toLocaleString()}원
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[#8C8780]">최저가</span>
                    <span className="text-[#C4956A] font-bold">
                      {item.minimumPrice.toLocaleString()}원
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[#8C8780]">사건번호</span>
                    <span className="text-[#5C5650] font-medium">
                      {item.caseNumber}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[#8C8780]">입찰일</span>
                    <span className="text-[#5C5650] font-medium">
                      {item.auctionDate}
                    </span>
                  </div>
                </div>

                {/* Gated Section — Blur for non-members */}
                <div className="relative mt-3 pt-3 border-t border-[#F0EDE8]">
                  <div className="blur-[4px] select-none pointer-events-none space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-[#8C8780]">
                        <i className="ri-user-line"></i>
                        <span>{item.hasTenant ? "임차인 있음" : "임차인 없음"}</span>
                      </div>
                      {item.expectedProfit != null ? (
                        <span className="text-xs font-semibold text-emerald-600">
                          예상수익 {item.expectedProfit.toLocaleString()}원
                        </span>
                      ) : (
                        // 시세 추정이 없으면 숫자를 만들어내지 않는다.
                        <span className="text-xs text-[#B8B3AE]">시세 분석 준비중</span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#8C8780] line-clamp-1">
                      {item.rightsAnalysis}
                    </div>
                  </div>
                  {/* Overlay CTA */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 rounded-lg z-10">
                    <i className="ri-lock-line text-[#C4956A] text-lg mb-1"></i>
                    <p className="text-xs font-semibold text-[#1C1917] mb-2 text-center leading-snug">
                      권리분석 · 수익률 예측은<br />회원 전용입니다
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate("/register");
                      }}
                      className="px-4 py-1.5 text-xs font-semibold bg-[#C4956A] text-white rounded-full hover:bg-[#B8845A] transition-all whitespace-nowrap cursor-pointer"
                    >
                      1초 간편가입하고 무료로 보기
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        triggerOpenChatbotWithMessage(buildAnalysisMessage(item), true);
                      }}
                      className="flex items-center justify-center gap-1 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer"
                      style={{
                        background: 'linear-gradient(135deg, #1C1917 0%, #3D2315 55%, #C4956A 100%)',
                        color: '#fff',
                      }}
                    >
                      <i className="ri-robot-2-line text-sm"></i>
                      AI 분석
                    </button>
                    <CalcDropdown itemId={item.id} />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/compare?items=${item.id}`);
                    }}
                    className="flex items-center justify-center gap-1.5 py-1.5 rounded-full text-xs font-medium border border-[#E8E5E0] text-[#5C5650] hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap"
                  >
                    <i className="ri-scales-3-line text-xs"></i>
                    비교에 추가
                  </button>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {/* 로딩 중에는 빈 상태를 띄우지 않는다 — 데이터가 오기 전에 "검색 결과가
          없습니다" 가 번쩍이면 조회 실패와 구분이 안 된다. */}
      {loading && filteredItems.length === 0 && (
        <div className="py-20 text-center">
          <div className="w-8 h-8 mx-auto border-2 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin" />
        </div>
      )}
      {!loading && filteredItems.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center py-20"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
            <i className="ri-search-line text-2xl text-[#C4956A]"></i>
          </div>
          <p className="text-[#5C5650] font-medium mb-1">
            검색 결과가 없습니다
          </p>
          <p className="text-sm text-[#8C8780]">
            {searchQuery
              ? "전문가 PICK 60건 안에는 없습니다. 아래 EchoTwin 전체 매물에서 찾아보세요."
              : "다른 검색어나 필터 조건으로 다시 시도해보세요"}
          </p>
        </motion.div>
      )}

      {/* EchoTwin 전체 매물 검색 — 큐레이션 60건 밖의 실제 매물, 항상 노출.
          사건번호·법원·강스터디 등급은 이 API가 안 줘서 카드 구성이 위
          "전문가 PICK"과 다르다. */}
      <div className="mt-12 pt-8 border-t border-[#E8E5E0]">
        <div className="flex items-center gap-2 mb-1">
          <i className="ri-global-line text-[#C4956A]"></i>
          <h3 className="text-sm font-bold text-[#1C1917]">EchoTwin 전체 매물</h3>
        </div>
        <p className="text-xs text-[#8C8780] mb-4">
          위 전문가 PICK 60건 밖에서, 같은 조건(
          {region !== "전체" ? region : "전체 지역"} ·{" "}
          {propertyType !== "전체" ? propertyType : "전체 물건"})으로 EchoTwin 전국 매물을 실시간 검색한
          결과입니다.
          {liveSearch.total > 0 && (
            <>
              {" "}
              총 <strong className="text-[#1C1917]">{liveSearch.total}</strong>건
              {liveSearch.total > liveSearch.items.length && ` (상위 ${liveSearch.items.length}건 표시)`}
            </>
          )}
        </p>

        {liveSearch.loading && (
          <div className="py-16 text-center">
            <div className="w-8 h-8 mx-auto border-2 border-[#E8E5E0] border-t-[#C4956A] rounded-full animate-spin" />
          </div>
        )}

        {liveSearch.error && (
          <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3">
            검색 실패: {liveSearch.error}
          </p>
        )}

        {!liveSearch.loading && !liveSearch.error && liveSearch.items.length === 0 && (
          <p className="text-xs text-[#8C8780]">이 조건에 맞는 매물이 없습니다.</p>
        )}

        {!liveSearch.loading && liveSearch.items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {liveSearch.items.map((c) => (
              <LiveCaseCard key={c.id} c={c} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}